import React, { useState } from 'react';
import { Upload, Trash2, Crop } from 'lucide-react';
import { ImageCropModal } from './ImageCropModal';
import type { CropData } from './ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageStyles, hasCropApplied } from './cropUtils';

interface BackgroundImageData {
  enabled?: boolean;
  url?: string;
  cloudfrontUrl?: string;
  position?: string;
  size?: string;
  repeat?: string;
  parallax?: boolean;
  crop?: CropData;
  overlay?: {
    enabled?: boolean;
    color?: string;
    opacity?: number;
  };
}

interface BackgroundImageEditorProps {
  backgroundImage?: BackgroundImageData;
  onChange: (backgroundImage: BackgroundImageData) => void;
  onOpenMediaUpload: () => void;
}

export const BackgroundImageEditor: React.FC<BackgroundImageEditorProps> = ({
  backgroundImage,
  onChange,
  onOpenMediaUpload,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const updateBackgroundImage = (updates: Partial<BackgroundImageData>) => {
    onChange({
      ...backgroundImage,
      ...updates,
    });
  };

  const toggleEnabled = (enabled: boolean) => {
    onChange({
      enabled,
      url: backgroundImage?.url || '',
      cloudfrontUrl: backgroundImage?.cloudfrontUrl || '',
      position: backgroundImage?.position || 'center',
      size: backgroundImage?.size || 'cover',
      repeat: backgroundImage?.repeat || 'no-repeat',
      crop: backgroundImage?.crop,
      overlay: backgroundImage?.overlay || {
        enabled: true,
        color: '#000000',
        opacity: 0.5,
      },
    });
  };

  const handleCropApply = (crop: CropData, _croppedAreaPixels: Area) => {
    updateBackgroundImage({ crop });
    setIsCropModalOpen(false);
  };

  const displayUrl = backgroundImage?.cloudfrontUrl || backgroundImage?.url;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Background Image (optional)
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={backgroundImage?.enabled || false}
            onChange={e => toggleEnabled(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">Enable background image</span>
        </label>
      </div>

      {backgroundImage?.enabled && (
        <div className="space-y-3 pl-4 border-l-2 border-gray-200">
          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image *</label>
            {displayUrl ? (
              <div className="flex items-start space-x-3">
                {/* Thumbnail with crop indicator */}
                <div className="relative group">
                  <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-300 bg-gray-100 relative">
                    <img
                      src={displayUrl}
                      alt="Background thumbnail"
                      className={
                        hasCropApplied(backgroundImage?.crop)
                          ? undefined
                          : 'w-full h-full object-cover'
                      }
                      style={
                        hasCropApplied(backgroundImage?.crop)
                          ? getCroppedImageStyles(backgroundImage?.crop)
                          : undefined
                      }
                    />
                  </div>
                  {/* Crop indicator badge */}
                  {backgroundImage?.crop && backgroundImage.crop.zoom !== 1 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
                      <Crop size={12} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setIsCropModalOpen(true)}
                      className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 flex items-center space-x-1"
                    >
                      <Crop size={14} />
                      <span>Crop / Adjust</span>
                    </button>
                    <button
                      type="button"
                      onClick={onOpenMediaUpload}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-1"
                    >
                      <Upload size={14} />
                      <span>Change</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        updateBackgroundImage({
                          url: '',
                          cloudfrontUrl: '',
                          crop: undefined,
                        })
                      }
                      className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center space-x-1"
                    >
                      <Trash2 size={14} />
                      <span>Remove</span>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 truncate" title={displayUrl}>
                    {displayUrl?.substring(0, 50)}...
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex space-x-2">
                <input
                  type="url"
                  value={backgroundImage?.url || ''}
                  onChange={e =>
                    updateBackgroundImage({
                      url: e.target.value,
                      cloudfrontUrl: '',
                    })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://example.com/image.jpg"
                />
                <button
                  type="button"
                  onClick={onOpenMediaUpload}
                  className="px-3 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center space-x-1"
                >
                  <Upload size={16} />
                  <span>Upload</span>
                </button>
              </div>
            )}
          </div>

          {/* Overlay Settings */}
          <OverlaySettings
            overlay={backgroundImage?.overlay}
            onChange={overlay =>
              updateBackgroundImage({
                overlay,
              })
            }
          />
        </div>
      )}

      {/* Crop Modal */}
      {displayUrl && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={displayUrl}
          initialCrop={backgroundImage?.crop}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
};

interface OverlaySettingsProps {
  overlay?: BackgroundImageData['overlay'];
  onChange: (overlay: BackgroundImageData['overlay']) => void;
}

const OverlaySettings: React.FC<OverlaySettingsProps> = ({ overlay, onChange }) => {
  const updateOverlay = (updates: Partial<NonNullable<BackgroundImageData['overlay']>>) => {
    onChange({
      enabled: overlay?.enabled ?? true,
      color: overlay?.color || '#000000',
      opacity: overlay?.opacity ?? 0.5,
      ...updates,
    });
  };

  return (
    <div className="space-y-2">
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          checked={overlay?.enabled !== false}
          onChange={e => updateOverlay({ enabled: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <span className="text-sm font-medium text-gray-700">
          Enable overlay (for text readability)
        </span>
      </label>

      {overlay?.enabled !== false && (
        <div className="grid grid-cols-2 gap-3 pl-6">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Overlay Color</label>
            <input
              type="color"
              value={overlay?.color || '#000000'}
              onChange={e => updateOverlay({ color: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Opacity: {((overlay?.opacity ?? 0.5) * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={overlay?.opacity ?? 0.5}
              onChange={e => updateOverlay({ opacity: parseFloat(e.target.value) })}
              className="w-full h-10"
            />
          </div>
        </div>
      )}
    </div>
  );
};
